import { useRef, useState, type PointerEvent } from "react";
import { useDrop } from "react-dnd";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { omit } from "lodash";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../../../../components/ui/tooltip";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { getGroupRowStyle } from "../../../../helpers/grid/getGroupRowStyle";
import { useGridDates } from "../../../../hooks/grid/useGridDates";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useFormatDragAndDropData } from "../../../../hooks/useFormatDragAndDropData";
import { useIsTemplate } from "../../../../hooks/useIsTemplate";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import { useCompanyDateFormat } from "../../../../../../../utils/hooks/company";
import useApi from "../../../../../../../utils/api";
import { getOrderedWeekdays } from "../../../../helpers/getOrderedWeekdays";
import { getScheduleUrl } from "../../../../helpers/getScheduleUrl";
import { getErrorMessage } from "../../../../../../../utils/helpers/errors";
import { getFormFormattedValues } from "../../../../helpers/form/getFormFormattedValues";
import { combineDateAndTime } from "../../../../../../../utils/helpers/date";
import { calculateShiftsToAssign } from "../../../../helpers/modals/calculateShiftsToAssign";
import { canAssignUnassignedShift, isUserAvailableForShift } from "../../../../helpers/data/canAssignUnassignedShift";
import { withLoadingToast } from "../../../../helpers/withLoadingToast";
import { getDayUnderSourceStart } from "../../../../helpers/grid/getDayUnderSourceStart";
import { getUnassignedPreviewOffset } from "../../../../components/UnassignedShifts/EventDisplay/CustomDragLayer";
import { getResourceUserId } from "../../../../helpers/data/groupedResourceId";
import type { Resource } from "../../../../types/Resource";

// interface DragItem {
//     id: string;
//     startDate: string;
//     endDate: string;
//     resourceId: string;
// }

// Last pointer buttons state seen over a cell, so the hover tooltip stays closed while
// the mouse is held down (e.g. drag-to-create sweeping across cells)
let pressedButtons = 0;

interface DroppableCellProps {
    resource: Resource;
    cellTime: string;
    gridColumnStart: number;
    gridRowStart: number;
    isCurrentTime?: boolean;
    withGroupDivider?: boolean;
    // cellWidth: number;
}

const DroppableCell = ({
    resource,
    cellTime,
    gridColumnStart,
    gridRowStart,
    isCurrentTime,
    withGroupDivider,
    // cellWidth,
}: DroppableCellProps) => {
    const { t } = useTranslation();
    const localRef = useRef<HTMLDivElement>(null);
    const [filters] = useFilters();
    const { authPut, authPost } = useApi();
    const { dispatch, dragAndDropCopy, type, unassignedEvents, templates } = useScheduleContext();
    const { gridStartTime, gridEndTime } = useGridDates();
    const { canCopy: canCopyEvent, canEdit: canEditEvent } = usePermissions();
    const userTimezone = moment.tz.guess(true);
    const formatDragAndDropData = useFormatDragAndDropData({ resource });
    const orderedWeekdays = getOrderedWeekdays();
    const isTemplate = useIsTemplate();
    const dateFormat = useCompanyDateFormat();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const createEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (data: any) => {
            const templateId = filters?.template?.id || templates?.[0]?.id;
            const url = getScheduleUrl(type, templateId);
            return authPost(url, { data: omit(data, ["id"]) });
        },
        onSuccess: (response: any) => {
            toast.success(dragAndDropCopy ? t("event-copied-successfully") : t("event-created-successfully"));
            dispatch({
                type: "create-event",
                payload: {
                    ...response,
                    resourceId: response.user?.id ?? response.user,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                }
            });
        },
        onError: (err) => getErrorMessage(err, t)
    });

    const editEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async (data: any) => {
            const templateId = filters?.template?.id || templates?.[0]?.id;
            const url = getScheduleUrl(type, templateId, data.id);
            const response = await authPut(url, { data });
            return { ...response, oldId: data.id }
        },
        onSuccess: (response: any) => {
            toast.success(t("event-updated-successfully"));
            const isSourceUnassigned = response.oldResourceId === "unassigned-shifts";
            if (isSourceUnassigned) {
                dispatch({ type: "delete-unassigned-event", payload: { id: response.oldId } });
            } else {
                dispatch({ type: "delete-event", payload: { id: response.oldId } });
            }

            dispatch({
                type: "create-event",
                payload: {
                    ...response,
                    resourceId: response.user?.id ?? response.user,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                }
            });
        },
        onError: (err) => getErrorMessage(err, t)
    });

    const assignMutation = useMutation({
        ...withLoadingToast(t("assigning")),
        mutationFn: async (data: any) => {
            const _data = omit(
                data,
                ["resourceId", "isUnassignedShift", "updatedAt", "createdAt", "_id", "__v", "id", "slots"]
            );
            const response = await authPut(
                `/unassigned-shifts/${data?.id}/assign`,
                { data: [getFormFormattedValues(_data)] }
            );
            return { response, data };
        },
        onSuccess: ({ response, data }: any) => {
            const message = response?.[0]?.message;
            const status = response?.[0]?.status;
            const shift = response?.[0]?.shift;

            if (!status) {
                toast.error(message);
                return;
            }

            toast.success(message);
            const timezone = moment.tz.guess();
            const startDate = moment.parseZone(shift.startDate).tz(timezone, true);
            const endDate = moment.parseZone(shift.endDate).tz(timezone, true);
            dispatch({
                type: "create-event",
                payload: { ...shift, startDate, endDate, resourceId: resource?.id, isSimple: data.isSimple },
            });
            const existingUnassignedEvent = (unassignedEvents as any).find((e: any) => e.id === data.id);
            const currentSlots = existingUnassignedEvent?.slots ?? data.slots;
            if (Number(currentSlots) === 1) {
                dispatch({ type: "delete-unassigned-event", payload: { id: data?.id } });
            } else {
                dispatch({
                    type: "update-unassigned-event",
                    payload: { ...(existingUnassignedEvent ?? data), slots: Number(currentSlots) - 1 }
                });
            }
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const [_, drop] = useDrop(() => ({
        accept: ["EVENT", "UNASSIGNED_EVENT"],
        drop: (item: any, monitor) => {
            const startOffset = monitor.getItemType() === "UNASSIGNED_EVENT"
                ? getUnassignedPreviewOffset(monitor.getClientOffset())
                : monitor.getSourceClientOffset();
            const targetDay = getDayUnderSourceStart(moment(cellTime), localRef.current, startOffset);
            const newStartDate = combineDateAndTime(targetDay, moment(item.startDate));
            let newEndDate = combineDateAndTime(targetDay, moment(item.endDate));
            if (moment(newEndDate).isBefore(moment(moment(newStartDate), "seconds"))) {
                newEndDate = moment(newEndDate).add(1, "day");
            }

            // Do not allow drop outside the grid dates
            if (newStartDate.isBefore(gridStartTime) || newEndDate.isAfter(gridEndTime)) return;
            if (item?.resourceId === "unassigned-shifts") {
                if (!canAssignUnassignedShift(item, resource)) {
                    toast.error(t("cannot-assign-shift-to-user"));
                    return;
                }
                // Build sections on the target day, not the shift's original date
                const calculatedValues = calculateShiftsToAssign({
                    ...item,
                    date: targetDay.format("YYYY-MM-DD"),
                    startDate: newStartDate,
                    endDate: newEndDate
                }, resource);
                if (!isUserAvailableForShift(resource, calculatedValues.startDate, calculatedValues.endDate)) {
                    toast.error(t("user-not-available-for-shift"));
                    return;
                }
                const formattedValues = getFormFormattedValues({
                    ...item,
                    ...calculatedValues,
                    startDate: newStartDate,
                    endDate: newEndDate
                });
                assignMutation.mutate({ ...formattedValues, user: getResourceUserId(resource?.id) });
                return;
            }
            const data = formatDragAndDropData(item, newStartDate, newEndDate);
            if (canCopyEvent && dragAndDropCopy) {
                const values = getFormFormattedValues(data);
                createEventMutation.mutate({
                    ...values,
                    date: isTemplate ? orderedWeekdays[moment(values.date).weekday()] : values?.date
                });
                return;
            }

            if (canEditEvent && !dragAndDropCopy) {
                const values = getFormFormattedValues(data);
                editEventMutation.mutate({
                    ...values,
                    date: isTemplate ? orderedWeekdays[moment(values.date).weekday()] : values?.date
                });
                return;
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
        canDrop: () => type !== "my-profile"
    }), [
        resource,
        cellTime,
        gridEndTime,
        gridStartTime,
        dragAndDropCopy,
        type,
        dispatch,
        isTemplate,
        canCopyEvent,
        canEditEvent,
        formatDragAndDropData,
    ]);

    const combinedRef = (node: HTMLDivElement | null) => {
        localRef.current = node;
        drop(node);
    };

    let conditionalStyles: any = isCurrentTime
        ? { backgroundColor: "#314a9f0d", borderBottom: "none" }
        : { backgroundColor: "transparent" };

    const isGroupRow = (resource as any)?.type === "view-by";

    if (isGroupRow) {
        conditionalStyles = getGroupRowStyle(withGroupDivider);
    }

    const cell = (
        <div
            ref={combinedRef}
            className="schedule-cell"
            onPointerMove={(e: PointerEvent) => {
                pressedButtons = e.buttons;
            }}
            style={{
                gridColumnStart: gridColumnStart,
                gridColumnEnd: gridColumnStart + 1,
                gridRowStart: gridRowStart,
                gridRowEnd: gridRowStart + 1,
                ...conditionalStyles
            }}
        />
    );

    if (isGroupRow) return cell;

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip
                open={isTooltipOpen}
                onOpenChange={(open) => setIsTooltipOpen(open && pressedButtons === 0)}
            >
                <TooltipTrigger asChild>
                    {cell}
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    align="start"
                    className="tw-bg-white tw-text-neutral-900 tw-border tw-border-neutral-200 tw-shadow-md tw-px-4 tw-py-3"
                >
                    <div className="tw-flex tw-gap-2 tw-items-center tw-text-sm tw-font-medium">
                        <Clock size={17} />
                        <span>{moment(cellTime).format(dateFormat)}</span>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default DroppableCell;